import { Box, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";

interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  align?: "left" | "center";
  id?: string;
  titleAs?: "h1" | "h2";
}

const SectionHeading: React.FC<SectionHeadingProps> = ({
  eyebrow,
  title,
  subtitle,
  align = "center",
  id,
  titleAs = "h2",
}) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.div
      initial={prefersReducedMotion ? false : "hidden"}
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
      variants={
        prefersReducedMotion
          ? undefined
          : {
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.11,
                  delayChildren: 0.06,
                },
              },
            }
      }
    >
      <Box sx={{ textAlign: align, mb: { xs: 2.1, md: 2.8 } }} id={id}>
        {eyebrow ? (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 12 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
            }}
          >
            <Typography
              component="p"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1.5,
                py: 0.5,
                borderRadius: "999px",
                background: "rgba(128,51,65,0.12)",
                border: "1px solid rgba(128,51,65,0.22)",
                color: "#5b2530",
                fontFamily: '"Manrope", sans-serif',
                fontSize: { xs: "0.68rem", sm: "0.74rem" },
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                mb: 0.9,
                width: "fit-content",
                mx: align === "center" ? "auto" : 0,
              }}
            >
              {eyebrow}
            </Typography>
          </motion.div>
        ) : null}

        <motion.div
          variants={{
            hidden: { opacity: 0, y: 18 },
            visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
          }}
        >
          <Typography
            component={titleAs}
            sx={{
              fontFamily: '"Cormorant Garamond", serif',
              color: "#2b1518",
              fontWeight: 700,
              fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.8rem" },
              lineHeight: 1.05,
              letterSpacing: "0.01em",
            }}
          >
            {title}
          </Typography>
        </motion.div>

        {subtitle ? (
          <motion.div
            variants={{
              hidden: { opacity: 0, y: 16 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            <Typography
              sx={{
                mt: 0.85,
                color: "rgba(48,28,31,0.8)",
                fontFamily: '"Manrope", sans-serif',
                fontSize: { xs: "0.92rem", sm: "0.99rem", md: "1.05rem" },
                lineHeight: 1.72,
                maxWidth: "74ch",
                mx: align === "center" ? "auto" : 0,
              }}
            >
              {subtitle}
            </Typography>
          </motion.div>
        ) : null}
      </Box>
    </motion.div>
  );
};

export default SectionHeading;
